import { AllocatedArea, Discount, GaArea } from "@types";
import { useTranslations } from "next-intl";
import { OrderStatus, PaymentStatus } from "./enums";

export function joinClassNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const currencyLookup: Record<
  string,
  { currency: string; locale: string }
> = {
  nz: { currency: "NZD", locale: "en-NZ" },
  us: { currency: "USD", locale: "en-US" },
  au: { currency: "AUD", locale: "en-AU" },
};

export function formatPercentage(locale: string, num: number | string) {
  const percentage = Number(String(num).replace("%", "")) / 100;
  return Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(percentage);
}

export function formatPrice(
  locale: string,
  currency: string,
  num: number | string,
  narrowSymbol?: boolean
) {
  if (String(num)?.includes("%")) {
    return formatPercentage(locale, num);
  }

  return Number(num) >= -1 && (num || num === 0)
    ? Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
        currencyDisplay: narrowSymbol ? "narrowSymbol" : "symbol",
      }).format(Number(num))
    : null;
}

export function range(start: number, stop: number, step = 1) {
  return Array.from(
    { length: (stop - start) / step + 1 },
    (_, i) => start + i * step
  );
}

export const toCapital = (string: string) => {
  if (!string?.trim()) return "";
  const words = string.split(" ");
  const upperWords = words.map(
    (word) => `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}`
  );
  return upperWords.join(" ");
};

export function getCookieValue(key: string, exact: boolean = true) {
  if (typeof window === "undefined" || !document.cookie) {
    return "";
  }

  const cookiePairs = document.cookie.split(";");

  const cookies: Record<string, string> = cookiePairs.reduce(
    (prevObj, currPair) => ({
      ...prevObj,
      [currPair.split("=")[0].trim()]: currPair
        .split("=")
        .slice(1)
        .join("=")
        .trim(),
    }),
    {}
  );

  const keyToMatch = exact
    ? key
    : Object.keys(cookies).find((v) => v.includes(key));

  if (keyToMatch) return cookies[keyToMatch];
}

export function handlePricesectionNameEqual(areas: {
  allocated: AllocatedArea[];
  ga: GaArea[];
  gaHasExtras: boolean;
}): boolean {
  const uniquePricesectionNames = new Set<string>();

  areas.allocated.forEach((allocatedArea) => {
    uniquePricesectionNames.add(allocatedArea.pricesection.name);
  });

  areas.ga.forEach((gaArea) => {
    uniquePricesectionNames.add(gaArea.pricesection.name);
  });

  const pricesectionNamesEqual = uniquePricesectionNames.size === 1;

  if (!pricesectionNamesEqual) {
    return false;
  }

  return true;
}

export function filterDiscountDups(discounts: Discount[]) {
  const uniqueDiscounts: Discount[] = [];
  const discountNames: Set<string> = new Set();

  for (const discount of discounts) {
    if (!discountNames.has(discount.name)) {
      discountNames.add(discount.name);
      uniqueDiscounts.push(discount);
    }
  }

  return uniqueDiscounts;
}

export function getTranslatedOrderStatus(
  status: string,
  translations: ReturnType<typeof useTranslations<"Utils.order_statuses">>
) {
  switch (status) {
    case OrderStatus.COMPLETE:
      return translations("complete");
    case OrderStatus.AWAITING:
      return translations("awaitingpayment");
    case OrderStatus.IN_REVIEW:
      return translations("inreview");
    case OrderStatus.ON_HOLD:
      return translations("onhold");
    case OrderStatus.PAYPLANACTIVE:
      return translations("payplanactive");
    default:
      return status;
  }
}

export function getTranslatedPaymentStatus(
  status: string,
  translations: ReturnType<typeof useTranslations<"Utils.payment_statuses">>
) {
  switch (status) {
    case PaymentStatus.PAID:
      return translations("paid");
    case PaymentStatus.INCOMPLETE:
      return translations("incomplete");
    case PaymentStatus.PENDING:
      return translations("pending");
    case PaymentStatus.PAY_AT_VENUE:
      return translations("payatvenue");
    case PaymentStatus.UNPAID:
      return translations("unpaid");
    case PaymentStatus.DEPOSITPAID:
      return translations("depositpaid");
    default:
      return status;
  }
}

// get cookiebot cookie
export function getCookieConsent() {
  try {
    const cookie = getCookieValue("CookieConsent");
    if (cookie) {
      // decode cookiebot cookie and make it json readable
      const decoded = JSON.parse(
        decodeURIComponent(cookie)
          .replace(/([a-zA-Z0-9_]+):/g, '"$1":')
          .replace(/'/g, '"')
      );
      return {
        marketing: decoded.marketing,
        preferences: decoded.preferences,
        statistics: decoded.statistics,
      };
    }
    throw new Error();
  } catch {
    return {
      marketing: false,
      preferences: false,
      statistics: false,
    };
  }
}

// Singleton promise to prevent multiple concurrent basket creation requests
let basketCreationPromise: Promise<string | null> | null = null;

/**
 * Calculates the retry delay in milliseconds from response headers.
 * Uses retry-after header if available, falls back to rl-twm (rate limit time window in minutes).
 * @returns Retry delay in milliseconds, minimum 1 second
 */
function getRetryDelayMs(headers: Headers): number {
  const retryAfter = headers.get("retry-after");
  if (retryAfter) {
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds)) {
      return Math.max(seconds, 1) * 1000;
    }
  }

  const rlTwm = headers.get("rl-twm");
  if (rlTwm) {
    const minutes = parseFloat(rlTwm);
    if (!isNaN(minutes)) {
      return Math.round(minutes * 60) * 1000;
    }
  }

  // Default to 5 seconds if no header is available
  return 5000;
}

/**
 * Creates a basket session if one doesn't exist.
 * Uses a singleton promise to prevent race conditions where multiple components
 * try to create a basket simultaneously.
 * Handles 429 (Too Many Requests) responses by waiting and retrying.
 * @param maxRetries Maximum number of retry attempts for 429 responses (default: 3)
 * @returns The basket key if successful, null otherwise
 */
export async function createBasketSession(
  maxRetries: number = 3
): Promise<string | null> {
  // Check if cookie already exists
  const existingKey = getCookieValue("ASP.NET_SessionId");
  if (existingKey) {
    return existingKey;
  }

  // If a request is already in flight, wait for it
  if (basketCreationPromise) {
    return basketCreationPromise;
  }

  // Start a new request
  basketCreationPromise = (async () => {
    let retryCount = 1;

    while (retryCount <= maxRetries) {
      try {
        const session_params = getCookieValue("itk_sessionparams") ?? "";
        const res = await fetch(`/api/basket`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(session_params),
        });

        if (res.ok) {
          const key = await res.json();
          if (key && typeof key === "string") {
            // Set the cookie client-side
            const expires = new Date(
              Date.now() + 24 * 60 * 60 * 1000
            ).toUTCString();
            document.cookie = `ASP.NET_SessionId=${key};expires=${expires};path=/`;
            return key;
          }
        }

        // Handle 429 Too Many Requests
        if (res.status === 429 && retryCount < maxRetries) {
          const delayMs = getRetryDelayMs(res.headers);
          retryCount++;
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }

        if (res.status === 429 && retryCount >= maxRetries) {
          return "max_retries";
        }

        return null;
      } catch {
        // On network error, don't retry - just return null
        return null;
      }
    }

    return null;
  })();

  try {
    return await basketCreationPromise;
  } finally {
    // Clear the promise so future calls can try again if needed
    basketCreationPromise = null;
  }
}
